import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {InputTextarea} from "primereact/inputtextarea";
import {useState} from "react";
import {useDispatch, useSelector} from "react-redux";

import {formatChangelogValue} from "@reva/shared/changelog";
import {layerToObjectTab} from "@reva/shared/changelog-layer";
import {SOURCE_TO_PERSISTENCE, SPF_PARAMS} from "@reva/shared/parameter-resolution";

import {useParameterValue} from "../../component/paramInput/paramHooks";
import {useRevaTranslation} from "../../hooks/languageHooks";
import {useNavigateWithQuery} from "../../hooks/navigationHooks";
import {selectCommentsForAttachments} from "../../redux/objectParametersSelectors";
import {ALL_SOURCES_TO_ROUTE} from "../../redux/objectParametersSlice";
import {selectReadOnly} from "../../redux/persistenceSlice";
import {saveSpfComment} from "../../redux/specialPropertyFeaturesThunk";
import PersistenceFinalMessage from "../object/PersistenceFinalMessage";
import SnapshotMessage from "../object/SnapshotMessage";

/**
 *
 * @returns {React.JSX.Element|null}
 */
export function TabChangelog() {
    const {t, language} = useRevaTranslation();
    const navigate = useNavigateWithQuery();
    const entries = useSelector(selectCommentsForAttachments);
    const readOnly = useSelector(selectReadOnly);
    const dispatch = useDispatch();
    if (!entries) {
        return null;
    }

    const formatter = (value, parameterDef) => formatChangelogValue(language, parameterDef, value);

    function TextEditor({options}) {
        const {parameter: rowParameter, commentParameter, commentForLayer, layer} = options.rowData;
        const parameter = commentParameter ?? rowParameter;
        const parameterType = SOURCE_TO_PERSISTENCE[layer];
        const {parameterValues, onValueChange} = useParameterValue(parameter, parameterType);
        const [commentValue, setCommentValue] = useState(commentParameter ? commentForLayer : parameterValues.comment);
        const rawParameterValue = parameterValues.allValues[layer];
        // Keine Kommentare für die Besichtigungsschicht
        if (parameterType === "INSPECTION") return null;
        const handleChange = (e) => {
            setCommentValue(e.target.value);
        };

        const onKeyDown = (e) => {
            if (e.key === "Enter") e.stopPropagation();
        };

        const handleBlur = () => {
            if (commentParameter === SPF_PARAMS.values) dispatch(saveSpfComment(commentValue || null));
            else onValueChange(rawParameterValue, commentValue);
        };

        return (
            <InputTextarea
                className="w-full"
                value={commentValue}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={onKeyDown}
                rows={2}
            />
        );
    }

    function renderLayerCell(rowData) {
        const route = ALL_SOURCES_TO_ROUTE[rowData.layer];
        const onClick = () => {
            if (!route) return;
            navigate(route);
        };
        return (
            <span onClick={onClick} className={"cursor-pointer"}>
                {layerToObjectTab(rowData.layer)}
            </span>
        );
    }

    return (
        <>
            <PersistenceFinalMessage />
            <SnapshotMessage />
            <DataTable
                value={entries}
                emptyMessage={t("value.emptyMessage")}
                removableSort
                size="small"
                stripedRows
                editMode={readOnly ? undefined : "cell"}
            >
                <Column
                    field="layer"
                    body={(rowData) => renderLayerCell(rowData)}
                    style={{
                        minWidth: "6em",
                        width: "20%",
                    }}
                    editor={null}
                    header={"Tab"}
                    sortable
                />
                <Column
                    field="parameterDef.nameDe"
                    style={{
                        minWidth: "6em",
                        width: "20%",
                    }}
                    header={"Parameter"}
                    sortable
                />
                <Column
                    field="changedFrom"
                    body={(rowData) => formatter(rowData.changedFrom, rowData.parameterDef)}
                    style={{
                        minWidth: "6em",
                        width: "20%",
                    }}
                    header={"Vorschlagswert"}
                    sortable
                />
                <Column
                    field="changedTo"
                    body={(rowData) => formatter(rowData.changedTo, rowData.parameterDef)}
                    style={{
                        minWidth: "6em",
                        width: "20%",
                    }}
                    header={"Geändert zu"}
                    sortable
                />

                <Column
                    field="commentForLayer"
                    style={{
                        minWidth: "16em",
                        width: "20%",
                    }}
                    header={"Kommentar"}
                    editor={(options) => <TextEditor options={options} />}
                    sortable
                />
            </DataTable>
        </>
    );
}

export default TabChangelog;
